import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { api } from "@/lib/api";

export default function TestimonialsSection() {
  const [reviews, setReviews] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api
      .get("/testimonials")
      .then((res) => setReviews(res.data || []))
      .catch(() => setReviews([]));
  }, []);

  useEffect(() => {
    if (paused || reviews.length < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % reviews.length), 9000);
    return () => clearInterval(t);
  }, [paused, reviews.length]);

  if (!reviews.length) return null;

  const current = reviews[index % reviews.length];
  const long = current.quote.length > 380;

  return (
    <section id="reviews" data-testid="testimonials-section" className="relative pt-28 pb-12 sm:pt-36 sm:pb-16 bg-[#100D12] overflow-hidden">
      <div className="absolute right-0 top-10 w-[26rem] h-[26rem] rounded-full bg-[#E890A7]/7 blur-[110px] pointer-events-none" />
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="text-center mb-16"
        >
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#E890A7]/80 mb-4">03 · Kind Words</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Clients, <em className="italic text-[#E890A7]">in their own words</em>
          </h2>
        </motion.div>

        <div
          data-testid="testimonial-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          className="relative rounded-[2rem] border border-[#E890A7]/15 bg-[#151218]/70 backdrop-blur-xl px-8 sm:px-16 py-14 sm:py-16 text-center"
        >
          <Quote size={36} className="mx-auto text-[#E890A7]/50 mb-5" />
          <div data-testid="testimonial-stars" className="flex items-center justify-center gap-1.5 mb-8">
            {[0, 1, 2, 3, 4].map((s) => (
              <Star key={s} size={16} className="text-[#E890A7] fill-[#E890A7]" />
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={current.testimonial_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <p
                data-testid="testimonial-quote"
                className={`font-display italic font-light text-slate-200 leading-relaxed ${
                  long ? "text-lg sm:text-xl" : "text-xl sm:text-2xl"
                }`}
              >
                &ldquo;{current.quote}&rdquo;
              </p>
              <footer className="mt-8">
                <p data-testid="testimonial-author" className="text-[#F2A7BA] font-semibold tracking-wide">
                  {current.author}
                </p>
                {current.context && (
                  <p className="text-xs font-mono tracking-[0.25em] uppercase text-slate-500 mt-2">{current.context}</p>
                )}
              </footer>
            </motion.blockquote>
          </AnimatePresence>

          <div className="mt-10 flex items-center justify-center gap-6">
            <button
              data-testid="testimonial-prev-btn"
              onClick={() => setIndex((i) => (i - 1 + reviews.length) % reviews.length)}
              className="w-10 h-10 rounded-full border border-[#E890A7]/30 flex items-center justify-center text-[#F2A7BA] hover:bg-[#E890A7] hover:text-[#0D0B0E] transition-all duration-300"
              aria-label="Previous review"
            >
              <ChevronLeft size={18} />
            </button>
            <span data-testid="testimonial-counter" className="font-mono text-xs tracking-[0.3em] text-slate-500">
              {String((index % reviews.length) + 1).padStart(2, "0")} / {String(reviews.length).padStart(2, "0")}
            </span>
            <button
              data-testid="testimonial-next-btn"
              onClick={() => setIndex((i) => (i + 1) % reviews.length)}
              className="w-10 h-10 rounded-full border border-[#E890A7]/30 flex items-center justify-center text-[#F2A7BA] hover:bg-[#E890A7] hover:text-[#0D0B0E] transition-all duration-300"
              aria-label="Next review"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
